import { createTheme } from "@mui/material/styles";
import variables from "./variables.json";

// Seus tokens extraídos do mockup
const designTokens = {
	shared: {
		typography: {
			fontFamily: variables.shared.typography.fontFamily.display,
			fontWeightBold: variables.shared.typography.fontWeight.bold,
		},
		borderRadius: 8, // MUI usa números para multiplicar pelo espaçamento base
	},
	dark: {
		primary: variables.themes.dark.colors.brand.primary,
		background: variables.themes.dark.colors.background.main,
		paper: variables.themes.dark.colors.surface.default,
		textPrimary: variables.themes.dark.colors.text.primary,
		textSecondary: variables.themes.dark.colors.text.secondary,
		border: variables.themes.dark.colors.border.default,
	},
	light: {
		primary: variables.themes.light.colors.brand.primary,
		background: variables.themes.light.colors.background.main,
		paper: variables.themes.light.colors.surface.default,
		textPrimary: variables.themes.light.colors.text.primary,
		textSecondary: variables.themes.light.colors.text.secondary,
		border: variables.themes.light.colors.border.default,
	},
};

export const getCustomTheme = (mode: "light" | "dark", colors?: { primary: string; secondary: string }) => {
	const tokens = mode === "dark" ? designTokens.dark : designTokens.light;
	const primaryColor = colors?.primary || tokens.primary;
	const secondaryColor = colors?.secondary;

	return createTheme({
		cssVariables: {
			colorSchemeSelector: "class",
			cssVarPrefix: "mui",
		},
		palette: {
			mode,
			primary: {
				main: primaryColor,
			},
			...(secondaryColor
				? {
						secondary: {
							main: secondaryColor,
						},
					}
				: {}),
			background: {
				default: tokens.background,
				paper: tokens.paper,
			},
			text: {
				primary: tokens.textPrimary,
				secondary: tokens.textSecondary,
			},
			divider: tokens.border,
		},
		typography: {
			fontFamily: designTokens.shared.typography.fontFamily,
			fontWeightBold: designTokens.shared.typography.fontWeightBold,
			allVariants: {
				color: tokens.textPrimary,
			},
		},
		shape: {
			borderRadius: designTokens.shared.borderRadius,
		},
		components: {
			MuiButton: {
				styleOverrides: {
					root: {
						textTransform: "none", // Remove o uppercase padrão do Material UI
						// fontWeight: designTokens.shared.typography.fontWeightBold,
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						"backgroundColor": mode === "dark" ? "rgba(0, 0, 0, 0.1)" : "rgba(0, 0, 0, 0.02)",
						"color": "var(--mui-palette-text-primary)",
						"borderRadius": 12,
						"transition": "all 0.2s",
						"& .MuiOutlinedInput-notchedOutline": {
							borderColor: "rgba(var(--mui-palette-text-primaryChannel) / 0.1)",
							borderWidth: "2px !important",
						},
						"&.Mui-focused .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline": {
							borderColor: "var(--mui-palette-primary-main)",
						},
						"& input": {
							paddingTop: "14.4px",
							paddingBottom: "14.4px",
						},
						"& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active": {
							WebkitTransition: '"color 999999999999999s ease-out, background-color 999999999999999s ease-out"',
							WebkitTransitionDelay: "999999999999999s",
						},
						"&.Mui-disabled": {
							"& .MuiOutlinedInput-notchedOutline": {
								borderColor: "rgba(var(--mui-palette-text-primaryChannel) / 0.1)",
							},

							"&:hover": {
								"& .MuiOutlinedInput-notchedOutline": {
									borderColor: "rgba(var(--mui-palette-text-primaryChannel) / 0.2)",
								},
							},
						},
						"&.Mui-error": {
							"& .MuiOutlinedInput-notchedOutline": {
								borderColor: "rgba(var(--mui-palette-error-mainChannel) / 0.7)",
							},

							"&:hover": {
								"& .MuiOutlinedInput-notchedOutline": {
									borderColor: "rgba(var(--mui-palette-error-mainChannel) / 1)",
								},
							},
						},
					},
				},
			},
			MuiInputLabel: {
				styleOverrides: {
					outlined: {
						// Esta é a posição dele em repouso (quando não tem foco e está vazio)
						// O segundo valor (14px) deve ser ajustado para alinhar com seu padding de 14.4px
						"transform": "translate(14px, 14px) scale(1)",

						"&.MuiInputLabel-shrink": {
							// Esta é a posição dele quando você clica ou digita algo
							// Mantemos o padrão do MUI ou ajustamos levemente se necessário
							transform: "translate(14px, -9px) scale(0.75)",
						},
					},
				},
			},
			MuiCssBaseline: {
				styleOverrides: {
					"body": {
						// "scrollbarColor": mode === "dark" ? "#6b6b6b #2b2b2b" : "#959595 #f5f5f5",
						"&::-webkit-scrollbar, & *::-webkit-scrollbar": {
							backgroundColor: "transparent",
							width: "6px",
							height: "6px",
						},
						"&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
							borderRadius: 8,
							backgroundColor: mode === "dark" ? "#6b6b6b" : "#959595",
							minHeight: 24,
							border: mode === "dark" ? "2px solid #6b6b6b" : "2px solid #959595",
						},
						"&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus": {
							backgroundColor: mode === "dark" ? "#959595" : "#6b6b6b",
						},
						"&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active": {
							backgroundColor: mode === "dark" ? "#959595" : "#6b6b6b",
						},
						"&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
							backgroundColor: mode === "dark" ? "#959595" : "#6b6b6b",
						},
						"&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner": {
							backgroundColor: mode === "dark" ? "#2b2b2b" : "#f5f5f5",
						},
					},
					"& .MuiPopper-root, & .MuiPopover-root": {
						"backgroundColor": "transparent",
						"& .MuiPaper-root": {
							"color": "var(--mui-palette-text-primary)",
							"borderRadius": "15px",
							"border": "1px solid rgba(var(--mui-palette-dividerChannel) / 0.5)",
							"backgroundColor": "rgba(var(--mui-palette-background-paperChannel) / 0.85)",
							"backdropFilter": "blur(10px)",

							"& .MuiPickersLayout-root": {
								backgroundColor: "transparent",
							},
						},
					},
				},
			},
		},
	});
};
